import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import usePrinter from '../../../hooks/hardware/usePrinter';
import {
  closeRegistrationModalAndReset,
  updatePrinterField,
} from '../../../store/slices/hardware/printerSlice';
import FormModal from '../../Modals/FormModal';
import InputField from '../../Forms/Input/InputField';
import SelectField from '../../Forms/Input/SelectField';
import { useBranch } from '../../../hooks/branches/useBranch';
import SimpleSearchSelect from '../../Forms/Input/SimpleSearchSelect';
import { codePageOptions } from '../../../constants/printerCodePages';
import PrinterSaleCopiesField from './PrinterSaleCopiesField';
import { getSaleCopiesValidationError } from '../../../features/printer/printerSaleCopies';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const PrinterRegistrationPopup: React.FC<Props> = ({ isOpen, onSave }) => {
  const dispatch = useDispatch();
  const { form } = usePrinter();
  const { fetchBranchDropdown, branchDropdown } = useBranch();

  useEffect(() => {
    if (isOpen) {
      fetchBranchDropdown();
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    let parsedValue: any = value;
    if (name === 'port' || name === 'branchId' || name === 'paperWidthMm' || name === 'saleCopies') {
      parsedValue = Number(value);
    }
    if (
      name === 'supportsQr' ||
      name === 'supportsPdf417' ||
      name === 'supportsDatamatrix' ||
      name === 'supportsCutter'
    ) {
      parsedValue = value === 'true';
    }
    dispatch(updatePrinterField({ field: name as any, value: parsedValue }));
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={() => dispatch(closeRegistrationModalAndReset())}
      title="Registrar Impresora"
      maxWidth="max-w-3xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave();
        }}
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
      >
        {/* Nombre */}
        <InputField
          label="Nombre de la Impresora"
          name="name"
          iconKey="printer"
          value={form.name}
          onChange={handleChange}
          type="text"
        />

        {/* Modelo */}
        <InputField
          label="Modelo"
          name="model"
          iconKey="printer"
          value={form.model}
          onChange={handleChange}
          type="text"
        />

        {/* Tipo */}
        <SelectField
          label="Tipo de Impresora"
          name="printerType"
          iconKey="printer"
          value={form.printerType}
          onChange={handleChange}
          options={[
            { label: 'Térmica', value: 'Térmica' },
            { label: 'Láser', value: 'Láser' },
          ]}
          placeholder="Seleccione tipo"
        />

        {/* IP */}
        <InputField
          label="Dirección IP"
          name="ipAddress"
          iconKey="network"
          value={form.ipAddress}
          onChange={handleChange}
          type="text"
        />

        {/* Puerto */}
        <InputField
          label="Puerto"
          name="port"
          iconKey="port"
          value={form.port.toString()}
          onChange={handleChange}
          type="number"
        />

        {/* Sucursal */}
        <SelectField
          label="Sucursal"
          name="branchId"
          iconKey="building"
          value={form.branchId ?? ''}
          onChange={handleChange}
          options={branchDropdown.map((branch) => ({
            label: branch.name,
            value: branch.id,
          }))}
          placeholder="Seleccione la sucursal"
        />

        {/* Ancho de papel */}
        <SelectField
          label="Ancho de Papel (mm)"
          name="paperWidthMm"
          iconKey="layout"
          value={form.paperWidthMm}
          onChange={handleChange}
          options={[
            { label: '58 mm', value: 58 },
            { label: '72 mm', value: 72 },
            { label: '80 mm', value: 80 },
          ]}
          placeholder="Seleccione ancho"
        />

        <SimpleSearchSelect
          label="Código de página"
          name="defaultCodePage"
          iconKey="code"
          options={codePageOptions}
          selectedValue={
            // busca el id de la opción cuyo `code` coincide con el form
            codePageOptions.find((opt) => opt.code === form.defaultCodePage)
              ?.id ?? null
          }
          onSelect={(selected) => {
            // 🔥 Guardamos solo el código
            dispatch(
              updatePrinterField({
                field: 'defaultCodePage',
                value: selected.code!,
              })
            );
          }}
          searchPlaceholder="Buscar código de página..."
          required
        />

        <PrinterSaleCopiesField
          value={form.saleCopies}
          onChange={(value) => dispatch(updatePrinterField({ field: 'saleCopies', value }))}
        />

        {/* Soporta QR */}
        <SelectField
          label="¿Soporta QR?"
          name="supportsQr"
          iconKey="qr"
          value={String(form.supportsQr)}
          onChange={handleChange}
          options={[
            { label: 'Sí', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
        />

        {/* Soporta PDF417 */}
        <SelectField
          label="¿Soporta PDF417?"
          name="supportsPdf417"
          iconKey="barcode"
          value={String(form.supportsPdf417)}
          onChange={handleChange}
          options={[
            { label: 'Sí', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
        />

        {/* Soporta DataMatrix */}
        <SelectField
          label="¿Soporta DataMatrix?"
          name="supportsDatamatrix"
          iconKey="barcode"
          value={String(form.supportsDatamatrix)}
          onChange={handleChange}
          options={[
            { label: 'Sí', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
        />

        {/* Soporta Cutter */}
        <SelectField
          label="¿Tiene cortador automático?"
          name="supportsCutter"
          iconKey="scissors"
          value={String(form.supportsCutter)}
          onChange={handleChange}
          options={[
            { label: 'Sí', value: 'true' },
            { label: 'No', value: 'false' },
          ]}
        />

        {/* Botón Guardar */}
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={Boolean(getSaleCopiesValidationError(form.saleCopies))}
            className="w-full rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            Guardar Impresora
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default PrinterRegistrationPopup;
